import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  FilterGlobalStore,
  type FilterOption,
} from '@bizzdesign/data-access-search';
import { TranslateDisplayTextPipe } from '@bizzdesign/shared/pipes';
import { type CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';

@Component({
  selector: 'bd-advanced-filter-options',
  imports: [
    AsyncPipe,
    NgClass,
    CheckboxModule,
    FormsModule,
    TranslateDisplayTextPipe,
  ],
  templateUrl: './advanced-filter-options.component.html',
  styleUrl: './advanced-filter-options.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedFilterOptionsComponent<SelectedType extends string> {
  readonly filterGlobalStore = inject(FilterGlobalStore);
  @Input() filterOptions: FilterOption<SelectedType>[] = [];
  @Input() isHorizontal: boolean = false;

  @Input() set selectedFilterOptions(selectedFilterOptions: SelectedType[]) {
    this._selectedFilterOptions = selectedFilterOptions;
    this.preparedOptions = selectedFilterOptions;
    this.filterOptions = this.filterOptions.map((filterOption) => ({
      ...filterOption,
      checked: this.selectedFilterOptions.some(
        (selectedFilterOption) => selectedFilterOption === filterOption.key,
      ),
    }));
  }
  get selectedFilterOptions(): SelectedType[] {
    return this._selectedFilterOptions;
  }

  private preparedOptions: SelectedType[] = [];
  private _selectedFilterOptions: SelectedType[] = [];

  @Output() submitSelectedFilters: EventEmitter<SelectedType[]> =
    new EventEmitter();

  constructor() {
    effect(() => {
      const autoSelectObjectScope =
        this.filterGlobalStore.autoSelectObjectScope();
      if (autoSelectObjectScope && this.filterOptions.length > 0) {
        const objectFilterOption = this.filterOptions.find(
          (option) => option.key === 'OBJECT',
        );
        if (objectFilterOption && !objectFilterOption.checked) {
          this.updateFilterOptions(
            { checked: true } as CheckboxChangeEvent,
            objectFilterOption,
          );
        }
      }
    });
  }

  updateFilterOptions(
    event: CheckboxChangeEvent,
    filterOption: FilterOption<SelectedType>,
  ) {
    filterOption.checked = event.checked;
    if (event.checked) {
      this.preparedOptions.push(filterOption.key);
    } else {
      this.preparedOptions = this.preparedOptions.filter(
        (selectedFilterOption) => selectedFilterOption !== filterOption.key,
      );
    }
    this.submitSelectedFilters.emit(this.preparedOptions);
  }
}
